/** Team Flow 侧边栏进入页自动打开目标的 sessionStorage key。 */
export const TEAM_FLOW_SIDEBAR_OPEN_STORAGE_KEY = 'team_flow_sidebar_open_storage_key';

/**
 * 侧边栏可被外部缓存唤起的功能入口。
 * 我的节点需精确到：preset（预设节点）/ favorite（我的收藏）/ hotTemplate（爆款模板）。
 */
export type TeamFlowSidebarOpenTarget =
  | 'addNode'
  | 'preset'
  | 'favorite'
  | 'hotTemplate'
  | 'history'
  | 'assetLibrary'
  | 'clipper';

/** 我的节点弹窗自动打开时的初始 Tab。 */
export interface TeamFlowMyNodeModalOpenOptions {
  defaultShellTab: 'myNodes' | 'hotTemplate';
  defaultTab: 'preset' | 'favorite';
}

const SIDEBAR_OPEN_TARGETS: ReadonlySet<string> = new Set([
  'addNode',
  'preset',
  'favorite',
  'hotTemplate',
  'history',
  'assetLibrary',
  'clipper',
]);

/**
 * 判断缓存值是否为合法的侧边栏自动打开目标。
 */
function isTeamFlowSidebarOpenTarget(value: string): value is TeamFlowSidebarOpenTarget {
  return SIDEBAR_OPEN_TARGETS.has(value);
}

/**
 * 将我的节点相关缓存目标解析为弹窗初始 Tab。
 */
export function resolveMyNodeModalOpenOptions(
  target: TeamFlowSidebarOpenTarget,
): TeamFlowMyNodeModalOpenOptions | null {
  switch (target) {
    case 'preset':
      return { defaultShellTab: 'myNodes', defaultTab: 'preset' };
    case 'favorite':
      return { defaultShellTab: 'myNodes', defaultTab: 'favorite' };
    case 'hotTemplate':
      return { defaultShellTab: 'hotTemplate', defaultTab: 'preset' };
    default:
      return null;
  }
}

/**
 * 写入侧边栏自动打开目标，供跨页跳入 Team Flow 后唤起对应弹窗。
 */
export function setTeamFlowSidebarOpenTarget(target: TeamFlowSidebarOpenTarget) {
  try {
    sessionStorage.setItem(TEAM_FLOW_SIDEBAR_OPEN_STORAGE_KEY, target);
  } catch (error) {
    console.warn('[TeamFlow Sidebar] set open target failed', error);
  }
}

/**
 * 读取侧边栏自动打开目标；不合法时返回 null，不主动清除。
 */
export function getTeamFlowSidebarOpenTarget(): TeamFlowSidebarOpenTarget | null {
  try {
    const value = sessionStorage.getItem(TEAM_FLOW_SIDEBAR_OPEN_STORAGE_KEY);
    if (!value) return null;
    if (!isTeamFlowSidebarOpenTarget(value)) return null;
    return value;
  } catch (error) {
    console.warn('[TeamFlow Sidebar] get open target failed', error);
    return null;
  }
}

/**
 * 清除侧边栏自动打开缓存。
 */
export function clearTeamFlowSidebarOpenTarget() {
  try {
    sessionStorage.removeItem(TEAM_FLOW_SIDEBAR_OPEN_STORAGE_KEY);
  } catch (error) {
    console.warn('[TeamFlow Sidebar] clear open target failed', error);
  }
}

/**
 * 读取并清除侧边栏自动打开目标；非法值一并清除。
 */
export function consumeTeamFlowSidebarOpenTarget(): TeamFlowSidebarOpenTarget | null {
  try {
    const value = sessionStorage.getItem(TEAM_FLOW_SIDEBAR_OPEN_STORAGE_KEY);
    if (!value) return null;

    sessionStorage.removeItem(TEAM_FLOW_SIDEBAR_OPEN_STORAGE_KEY);
    if (!isTeamFlowSidebarOpenTarget(value)) return null;
    return value;
  } catch (error) {
    console.warn('[TeamFlow Sidebar] consume open target failed', error);
    return null;
  }
}
